import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Plus, Download, ArrowDownCircle, ArrowUpCircle, X, Search, ArrowUp, ArrowDown, Pencil, Landmark, CreditCard, CheckCircle2, AlertTriangle, RefreshCw, Zap } from "lucide-react";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

type Row = {
  date: string;
  direction: "in" | "out";
  amountPence: number;
  description: string;
  category: string;
  source: string;
  shopId: number | null;
  customerId: number | null;
  driverId: number | null;
  shopName?: string | null;
  customerName?: string | null;
  driverName?: string | null;
  providerSourceReference?: string | null;
  reference: string | null;
  loanKind?: "loan" | "repayment" | null;
  lenderId?: number | null;
  stripeConfirmed?: boolean;
  memo?: boolean;
};
type Lender = { id: number; name: string; isDirector: boolean };
type LoanBalance = { lenderId: number; name: string; isDirector: boolean; outstandingPence: number; loanedPence: number; repaidPence: number };

type OrderMatch = { orderId: number; dateISO: string; paymentIntentId: string | null; bookFeePence: number; stripeFeePence: number | null; status: "matched" | "amount_mismatch" | "missing_in_stripe" };
type ExtraFee = { feeId: string; paymentIntentId: string | null; amountPence: number; dateISO: string };
type Payout = { id: string; arrivalISO: string; method: "standard" | "instant"; amountPence: number; instantFeePence: number; status: string };
type Recon = {
  ok: boolean; error?: string;
  orders: OrderMatch[]; extraStripeFees: ExtraFee[];
  totals: { bookCardIncomePence: number; stripeFeesCollectedPence: number; matchedFeesPence: number; totalOrders: number; matchedCount: number; mismatchCount: number; missingCount: number; extraCount: number };
  payouts: Payout[];
  payoutTotals: { paidOutToBankPence: number; instantFeesPence: number; standardCount: number; instantCount: number };
  balance: { availablePence: number; pendingPence: number; inTransitPence: number } | null;
};

function daysAgoIso(n: number) {
  return new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);
}

export default function AdminCashbook() {
  useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const { toast } = useToast();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [from, setFrom] = useState(daysAgoIso(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState({ in: 0, out: 0, net: 0, tradingIn: 0, tradingOut: 0, tradingNet: 0, financingIn: 0, financingOut: 0 });
  const [loading, setLoading] = useState(false);
  const [stripeReceiptEvidence,setStripeReceiptEvidence]=useState<string>("unavailable");
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState("");
  const [entryType, setEntryType] = useState<"all" | "system" | "manual">("all");
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [query, entryType, sortDir, from, to, pageSize, rows]);

  // manual entry form (also used for editing)
  const [editingId, setEditingId] = useState<number | null>(null);
  const [dir, setDir] = useState<"in" | "out">("out");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("");
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [loanKind, setLoanKind] = useState<"" | "loan" | "repayment">("");
  const [lenderId, setLenderId] = useState<number | "">("");
  const [saving, setSaving] = useState(false);

  // lenders / directors' loans
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [balances, setBalances] = useState<LoanBalance[]>([]);
  const [newLender, setNewLender] = useState("");
  const lenderName = (id: number | null | undefined) => lenders.find(l => l.id === id)?.name ?? "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/cashbook?from=${from}T00:00:00Z&to=${to}T23:59:59Z`, { headers: authHeaders });
      const d = await r.json() as { stripeReceiptEvidence?:string;rows: Row[]; totalInPence: number; totalOutPence: number; netPence: number; tradingInPence?: number; tradingOutPence?: number; tradingNetPence?: number; financingInPence?: number; financingOutPence?: number };
      setRows(d.rows ?? []);
      setStripeReceiptEvidence(d.stripeReceiptEvidence??"unavailable");
      setTotals({
        in: d.totalInPence ?? 0, out: d.totalOutPence ?? 0, net: d.netPence ?? 0,
        tradingIn: d.tradingInPence ?? d.totalInPence ?? 0,
        tradingOut: d.tradingOutPence ?? d.totalOutPence ?? 0,
        tradingNet: d.tradingNetPence ?? d.netPence ?? 0,
        financingIn: d.financingInPence ?? 0,
        financingOut: d.financingOutPence ?? 0,
      });
    } catch {
      toast({ title: "Could not load cashbook", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const loadLendersAndBalances = useCallback(async () => {
    try {
      const [lr, br] = await Promise.all([
        fetch("/api/admin/cashbook/lenders", { headers: authHeaders }),
        fetch("/api/admin/cashbook/loan-balances", { headers: authHeaders }),
      ]);
      if (lr.ok) setLenders((await lr.json() as { lenders: Lender[] }).lenders ?? []);
      if (br.ok) setBalances((await br.json() as { balances: LoanBalance[] }).balances ?? []);
    } catch { /* non-fatal */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void loadLendersAndBalances(); }, [loadLendersAndBalances]);

  // A loan is money in; a repayment is money out — keep direction consistent.
  useEffect(() => {
    if (loanKind === "loan") setDir("in");
    else if (loanKind === "repayment") setDir("out");
  }, [loanKind]);

  function resetForm() {
    setEditingId(null); setDir("out"); setAmount(""); setDesc(""); setCategory("");
    setEntryDate(new Date().toISOString().slice(0, 10)); setLoanKind(""); setLenderId("");
  }

  function startEdit(r: Row) {
    if (!r.reference?.startsWith("manual:")) return;
    setEditingId(Number(r.reference.split(":")[1]));
    setDir(r.direction);
    setAmount((r.amountPence / 100).toFixed(2));
    setDesc(r.description);
    setCategory(r.category === "Manual" ? "" : r.category);
    setEntryDate(r.date.slice(0, 10));
    setLoanKind(r.loanKind ?? "");
    setLenderId(r.lenderId ?? "");
    setShowAdd(true);
  }

  async function saveManual() {
    const pounds = parseFloat(amount);
    if (!(pounds > 0)) { toast({ title: "Enter an amount", variant: "destructive" }); return; }
    if (!desc.trim()) { toast({ title: "Enter a description", variant: "destructive" }); return; }
    if (loanKind && !lenderId) { toast({ title: "Choose the lender/director for this loan", variant: "destructive" }); return; }
    const amountPence = Math.round(pounds * 100);
    // Duplicate check only on NEW entries.
    if (editingId === null) {
      const dupe = rows.find(r =>
        r.date.slice(0, 10) === entryDate && r.direction === dir &&
        r.amountPence === amountPence && r.description.trim().toLowerCase() === desc.trim().toLowerCase());
      if (dupe && !confirm(`This looks like a possible duplicate — ${dir === "in" ? "money in" : "money out"} of ${pence(amountPence)} on ${entryDate} with the same description is already in the cashbook.\n\nIs this a duplicate record?\n\nCancel to stop, OK to add anyway.`)) return;
    }
    setSaving(true);
    try {
      const body = JSON.stringify({
        direction: dir, amountPence, description: desc.trim(), category: category.trim() || undefined, entryDate,
        loanKind: loanKind || null, lenderId: loanKind ? lenderId : null,
      });
      const url = editingId === null ? "/api/admin/cashbook/manual" : `/api/admin/cashbook/manual/${editingId}`;
      const method = editingId === null ? "POST" : "PATCH";
      const r = await fetch(url, { method, headers: { "Content-Type": "application/json", ...authHeaders }, body });
      if (!r.ok) throw new Error((await r.json() as { error?: string }).error ?? "Failed");
      toast({ title: editingId === null ? "Entry added to cashbook" : "Entry updated", description: `${dir === "in" ? "Money in" : "Money out"} ${pence(amountPence)} — ${desc.trim()}` });
      resetForm(); setShowAdd(false);
      void load(); void loadLendersAndBalances();
    } catch (err) {
      toast({ title: "Could not save entry", description: (err as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function addLender() {
    const name = newLender.trim();
    if (name.length < 2) return;
    try {
      const r = await fetch("/api/admin/cashbook/lenders", { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders }, body: JSON.stringify({ name }) });
      if (!r.ok) throw new Error("Failed");
      const { lender } = await r.json() as { lender: Lender };
      setNewLender("");
      await loadLendersAndBalances();
      setLenderId(lender.id);
      toast({ title: `Added ${name}` });
    } catch {
      toast({ title: "Could not add lender", variant: "destructive" });
    }
  }

  async function voidEntry(reference: string | null) {
    if (!reference?.startsWith("manual:")) return;
    const id = reference.split(":")[1];
    if (!confirm("Void this manual entry?")) return;
    try {
      const r = await fetch(`/api/admin/cashbook/manual/${id}/void`, { method: "PATCH", headers: authHeaders });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Entry voided" });
      void load(); void loadLendersAndBalances();
    } catch {
      toast({ title: "Could not void", variant: "destructive" });
    }
  }

  async function downloadCsv() {
    try {
      const r = await fetch(`/api/admin/cashbook.csv?from=${from}T00:00:00Z&to=${to}T23:59:59Z`, { headers: authHeaders });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `cashbook_${from}_${to}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  }

  const attribution = (r: Row) =>
    r.loanKind ? `${r.loanKind === "loan" ? "Loan from" : "Repaid to"} ${lenderName(r.lenderId) || "—"}`
    : r.shopId ? (r.shopName || `Shop #${r.shopId}`)
    : r.driverId ? (r.driverName || `Driver #${r.driverId}`)
    : r.customerId ? (r.customerName || `Customer #${r.customerId}`)
    : "—";

  const q = query.trim().toLowerCase();
  const qNum = q.replace(/[£\s,]/g, ""); // amount search: "£80", "80.00", "80" all match
  const visibleRows = rows
    .filter(r => entryType === "all" || (entryType === "manual" ? r.source === "manual" : r.source !== "manual"))
    .filter(r => !q ||
      r.description.toLowerCase().includes(q) ||
      (r.category ?? "").toLowerCase().includes(q) ||
      attribution(r).toLowerCase().includes(q) ||
      (r.reference ?? "").toLowerCase().includes(q) ||
      (r.providerSourceReference ?? "").toLowerCase().includes(q) ||
      (qNum !== "" && (r.amountPence / 100).toFixed(2).includes(qNum)))
    .sort((a, b) => sortDir === "desc" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date));
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = visibleRows.slice(pageStart, pageStart + pageSize);
  const filteredIn = visibleRows.filter(r => r.direction === "in" && !r.memo).reduce((s, r) => s + r.amountPence, 0);
  const filteredOut = visibleRows.filter(r => r.direction === "out" && !r.memo).reduce((s, r) => s + r.amountPence, 0);

  const [openingBal, setOpeningBal] = useState("");
  const [actualBal, setActualBal] = useState("");
  const [savedAsOf, setSavedAsOf] = useState<string | null>(null);
  const [savingOpening, setSavingOpening] = useState(false);

  useEffect(() => {
    void fetch("/api/admin/cashbook/opening-balance", { headers: authHeaders })
      .then(async r => {
        if (!r.ok) return;
        const d = await r.json() as { pence: number | null; asOfDate: string | null };
        if (d.pence != null) setOpeningBal((d.pence / 100).toFixed(2));
        setSavedAsOf(d.asOfDate);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveOpening() {
    const p = Math.round((parseFloat(openingBal) || 0) * 100);
    setSavingOpening(true);
    try {
      const r = await fetch("/api/admin/cashbook/opening-balance", {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ pence: p, asOfDate: from }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json() as { asOfDate: string };
      setSavedAsOf(d.asOfDate);
      toast({ title: "Opening balance saved" });
    } catch {
      toast({ title: "Could not save opening balance", variant: "destructive" });
    } finally {
      setSavingOpening(false);
    }
  }

  // Stripe reconciliation — loaded on demand (it pages the live Stripe API).
  const [recon, setRecon] = useState<Recon | null>(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [showAllMatches, setShowAllMatches] = useState(false);
  async function loadRecon() {
    setReconLoading(true); setRecon(null);
    try {
      const r = await fetch(`/api/admin/cashbook/stripe-reconciliation?from=${from}T00:00:00Z&to=${to}T23:59:59Z`, { headers: authHeaders });
      const d = await r.json() as Recon;
      setRecon(d);
      if (!d.ok) toast({ title: "Stripe reconciliation problem", description: d.error ?? "Could not load", variant: "destructive" });
    } catch {
      toast({ title: "Could not reach Stripe", variant: "destructive" });
    } finally {
      setReconLoading(false);
    }
  }

  const totalOwedToLenders = balances.reduce((s, b) => s + Math.max(0, b.outstandingPence), 0);

  return (
    <AdminLayout>
      {stripeReceiptEvidence!=="verified" && <p role="status" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">Stripe cash receipts are unavailable. Cash totals are incomplete; missing receipts are not zero income or costs.</p>}
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Cashbook</h1>
            <p className="text-sm text-slate-500">Every money in and out — attributed, for the bookkeeper.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { resetForm(); setShowAdd(s => !s); }} className="flex items-center gap-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-lg">
              <Plus size={15} /> Add entry
            </button>
            <button onClick={() => void downloadCsv()} className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-lg">
              <Download size={15} /> Full CSV
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-4">
          <label className="text-sm text-slate-600">From <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="ml-1 border rounded px-2 py-1 text-sm" /></label>
          <label className="text-sm text-slate-600">To <input type="date" value={to} onChange={e => setTo(e.target.value)} className="ml-1 border rounded px-2 py-1 text-sm" /></label>
          <label className="text-sm text-slate-600">Entry type
            <select value={entryType} onChange={e => setEntryType(e.target.value as "all" | "system" | "manual")} className="ml-1 border rounded px-2 py-1.5 text-sm">
              <option value="all">All entries</option>
              <option value="system">System entries</option>
              <option value="manual">Manual entries</option>
            </select>
          </label>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search description, category, attribution, or amount (e.g. 80)…" className="w-full border rounded pl-8 pr-8 py-1.5 text-sm" />
            {query && <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><ArrowDownCircle size={13} /> Recorded cash in</div>
            <div className="text-xl font-bold text-emerald-800">{pence(totals.tradingIn)}</div>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
            <div className="text-xs text-rose-700 font-semibold flex items-center gap-1"><ArrowUpCircle size={13} /> Recorded cash out</div>
            <div className="text-xl font-bold text-rose-800">{pence(totals.tradingOut)}</div>
          </div>
          <div className={`rounded-xl border p-3 ${totals.tradingNet >= 0 ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}>
            <div className="text-xs font-semibold text-slate-600">{totals.tradingNet >= 0 ? "Net cash movement" : "Net cash movement"}</div>
            <div className={`text-xl font-bold ${totals.tradingNet >= 0 ? "text-emerald-800" : "text-rose-800"}`}>{totals.tradingNet >= 0 ? "▲ " : "▼ "}{pence(Math.abs(totals.tradingNet))}</div>
          </div>
        </div>
        <p className="text-xs text-slate-600 mb-3">Cash movement is not profit. Amounts owed to drivers, shops and kindness remain liabilities. Bank arrival and opening balances require separate evidence.</p>
        {/* Financing sits below the trading line — real cash, but loans aren't trading.
            The cash net (incl. loans) is what reconciles to the bank. */}
        <div className="text-[11px] text-slate-500 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>Excludes financing:</span>
          <span className="text-indigo-700 font-medium">Loans in +{pence(totals.financingIn)}</span>
          <span className="text-indigo-700 font-medium">Repayments out −{pence(totals.financingOut)}</span>
          <span className="text-slate-300">|</span>
          <span>Recorded net including loans: <span className={`font-semibold ${totals.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{totals.net >= 0 ? "+" : "−"}{pence(Math.abs(totals.net))}</span></span>
        </div>

        {/* Directors' loans */}
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 mb-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><Landmark size={15} className="text-indigo-600" /> Directors&apos; loans</h3>
            <span className="text-sm text-slate-600">Total still owed to lenders: <span className="font-bold text-indigo-800">{pence(totalOwedToLenders)}</span></span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {balances.map(b => (
              <div key={b.lenderId} className="rounded-lg bg-white border border-slate-200 px-3 py-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{b.name}{b.isDirector && <span className="ml-1 text-[10px] text-indigo-600">director</span>}</div>
                  <div className="text-[11px] text-slate-400">Lent {pence(b.loanedPence)} · Repaid {pence(b.repaidPence)}</div>
                </div>
                <div className={`text-sm font-bold ${b.outstandingPence > 0 ? "text-indigo-800" : "text-slate-400"}`}>{pence(Math.max(0, b.outstandingPence))}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <input value={newLender} onChange={e => setNewLender(e.target.value)} placeholder="Add a lender (name)…" className="border rounded px-2 py-1.5 text-sm" />
            <button onClick={() => void addLender()} disabled={newLender.trim().length < 2} className="text-sm font-semibold text-indigo-700 border border-indigo-200 hover:bg-indigo-50 px-3 py-1.5 rounded-lg disabled:opacity-40">Add lender</button>
          </div>
        </div>

        {/* Bank reconciliation */}
        <div className="rounded-xl border border-slate-200 p-4 mb-4 bg-white">
          <h3 className="font-semibold text-slate-800 mb-1">Bank reconciliation — review required</h3>
          <p className="text-xs text-slate-500 mb-3">Opening and closing amounts are notes until matched to bank statements. Stripe receipts include money still held at Stripe; adding them to an opening bank balance does not establish a bank closing balance.</p>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="text-sm">
              <div>Opening balance (£)</div>
              <div className="flex items-center gap-2 mt-1">
                <input value={openingBal} onChange={e => setOpeningBal(e.target.value)} inputMode="decimal" placeholder="0.00" className="w-28 border rounded px-2 py-1.5" />
                <button onClick={() => void saveOpening()} disabled={savingOpening} className="text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 px-2 py-1.5 rounded disabled:opacity-50">{savingOpening ? "Saving…" : "Save"}</button>
              </div>
              {savedAsOf && <div className="text-[11px] text-slate-400 mt-0.5">Saved as of {savedAsOf}</div>}
            </div>
            <div className="text-sm text-amber-800 pb-1">Closing bank balance: unverified</div>
            <label className="text-sm">Actual bank balance now (£)
              <input value={actualBal} onChange={e => setActualBal(e.target.value)} inputMode="decimal" placeholder="0.00" className="mt-1 w-36 border rounded px-2 py-1.5 block" />
            </label>
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2">Statement reconciliation required</div>
          </div>
        </div>

        {/* Reconcile against real Stripe data */}
        <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><CreditCard size={15} className="text-violet-600" /> Reconcile against Stripe</h3>
              <p className="text-xs text-slate-500">Matches every card order to its real Stripe application fee, and lists provider payout receipts (bank arrival remains unverified). Uses live Stripe data for {from} → {to}.</p>
            </div>
            <button onClick={() => void loadRecon()} disabled={reconLoading} className="flex items-center gap-1.5 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 px-3 py-2 rounded-lg disabled:opacity-50">
              <RefreshCw size={14} className={reconLoading ? "animate-spin" : ""} /> {reconLoading ? "Checking Stripe…" : recon ? "Re-check" : "Check against Stripe"}
            </button>
          </div>

          {recon?.ok && (() => {
            const t = recon.totals, pt = recon.payoutTotals;
            const diff = t.bookCardIncomePence - t.stripeFeesCollectedPence; // + = books above Stripe
            const clean = t.mismatchCount === 0 && t.missingCount === 0 && t.extraCount === 0;
            return (
              <div className="mt-4 space-y-3">
                {/* income match */}
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Card income — the books</div>
                    <div className="text-lg font-bold text-slate-800">{pence(t.bookCardIncomePence)}</div>
                    <div className="text-[11px] text-slate-400">{t.totalOrders} card orders</div>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Fees Stripe actually collected</div>
                    <div className="text-lg font-bold text-violet-800">{pence(t.stripeFeesCollectedPence)}</div>
                    <div className="text-[11px] text-slate-400">real application fees</div>
                  </div>
                  <div className={`rounded-lg border p-3 ${Math.abs(diff) < 100 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                    <div className="text-[11px] font-semibold text-slate-600">Difference</div>
                    <div className={`text-lg font-bold ${Math.abs(diff) < 100 ? "text-emerald-800" : "text-amber-800"}`}>{diff === 0 ? "£0.00 — exact" : `${diff > 0 ? "Books +" : "Stripe +"}${pence(Math.abs(diff))}`}</div>
                    <div className="text-[11px] text-slate-400">{diff > 0 ? "books count more than Stripe took" : diff < 0 ? "Stripe took more than the books show" : "matched"}</div>
                  </div>
                </div>

                {/* per-record match status */}
                <div className={`rounded-lg border px-3 py-2 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${clean ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-700"}`}>
                  <span className="flex items-center gap-1 font-semibold"><CheckCircle2 size={14} className="text-emerald-600" /> {t.matchedCount} matched exactly</span>
                  {t.mismatchCount > 0 && <span className="flex items-center gap-1 text-amber-700"><AlertTriangle size={14} /> {t.mismatchCount} amount differs</span>}
                  {t.missingCount > 0 && <span className="flex items-center gap-1 text-rose-700"><X size={14} /> {t.missingCount} in books, not in Stripe</span>}
                  {t.extraCount > 0 && <span className="flex items-center gap-1 text-indigo-700"><Plus size={14} /> {t.extraCount} in Stripe, not in books</span>}
                  {clean && <span className="font-semibold">— every record confirmed against Stripe ✓</span>}
                </div>

                {/* payouts + bank + early payout fees */}
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Stripe payouts marked paid</div>
                    <div className="text-lg font-bold text-slate-800">{pence(pt.paidOutToBankPence)}</div>
                    <div className="text-[11px] text-slate-400">{pt.standardCount} standard · {pt.instantCount} early (instant)</div>
                  </div>
                  <div className={`rounded-lg border p-3 ${pt.instantFeesPence > 0 ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}`}>
                    <div className="text-[11px] text-slate-500 font-semibold flex items-center gap-1"><Zap size={12} className="text-rose-500" /> Early-payout fees (out to Stripe)</div>
                    <div className={`text-lg font-bold ${pt.instantFeesPence > 0 ? "text-rose-800" : "text-slate-800"}`}>{pence(pt.instantFeesPence)}</div>
                    <div className="text-[11px] text-slate-400">Stripe&apos;s charge for instant payouts</div>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <div className="text-[11px] text-slate-500 font-semibold">Still held by Stripe (in transit)</div>
                    <div className="text-lg font-bold text-slate-800">{recon.balance ? pence(recon.balance.inTransitPence) : "—"}</div>
                    <div className="text-[11px] text-slate-400">Current Stripe balance; funds may be owed to others</div>
                  </div>
                </div>

                {/* payouts list */}
                {recon.payouts.length > 0 && (
                  <details className="rounded-lg bg-white border border-slate-200">
                    <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700">Payouts to bank ({recon.payouts.length})</summary>
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[520px]">
                      <thead className="bg-slate-50 text-slate-500 text-xs"><tr><th className="text-left px-3 py-1.5">Arrived</th><th className="text-left px-3 py-1.5">Type</th><th className="text-right px-3 py-1.5">Amount</th><th className="text-right px-3 py-1.5">Instant fee</th><th className="text-left px-3 py-1.5">Status</th></tr></thead>
                      <tbody>
                        {recon.payouts.map(p => (
                          <tr key={p.id} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{p.arrivalISO.slice(0, 10)}</td>
                            <td className="px-3 py-1.5">{p.method === "instant" ? <span className="text-rose-700 font-medium">Early (instant)</span> : "Standard"}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{pence(p.amountPence)}</td>
                            <td className="px-3 py-1.5 text-right text-rose-700">{p.instantFeePence > 0 ? pence(p.instantFeePence) : ""}</td>
                            <td className="px-3 py-1.5 text-slate-500">{p.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </details>
                )}

                {/* per-order matches — problems first, all on toggle */}
                <details className="rounded-lg bg-white border border-slate-200">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700">Per-order match ({recon.orders.length} orders{recon.extraStripeFees.length ? ` · ${recon.extraStripeFees.length} extra Stripe fees` : ""})</summary>
                  <div className="px-3 pt-1 pb-2 flex items-center gap-3 text-xs">
                    <label className="flex items-center gap-1.5 text-slate-600"><input type="checkbox" checked={showAllMatches} onChange={e => setShowAllMatches(e.target.checked)} /> Show matched orders too</label>
                  </div>
                  <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0"><tr><th className="text-left px-3 py-1.5">Date</th><th className="text-left px-3 py-1.5">Order</th><th className="text-right px-3 py-1.5">Books</th><th className="text-right px-3 py-1.5">Stripe</th><th className="text-left px-3 py-1.5">Status</th></tr></thead>
                      <tbody>
                        {recon.orders.filter(o => showAllMatches || o.status !== "matched").map(o => (
                          <tr key={o.orderId} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{o.dateISO.slice(0, 10)}</td>
                            <td className="px-3 py-1.5">#{o.orderId}</td>
                            <td className="px-3 py-1.5 text-right">{pence(o.bookFeePence)}</td>
                            <td className="px-3 py-1.5 text-right">{o.stripeFeePence == null ? "—" : pence(o.stripeFeePence)}</td>
                            <td className="px-3 py-1.5">
                              {o.status === "matched" && <span className="text-emerald-700">✓ matched</span>}
                              {o.status === "amount_mismatch" && <span className="text-amber-700">⚠ differs</span>}
                              {o.status === "missing_in_stripe" && <span className="text-rose-700">✗ not in Stripe</span>}
                            </td>
                          </tr>
                        ))}
                        {recon.extraStripeFees.map(e => (
                          <tr key={e.feeId} className="border-t border-slate-100 bg-indigo-50/30">
                            <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{e.dateISO.slice(0, 10)}</td>
                            <td className="px-3 py-1.5 text-indigo-700">Stripe fee (no order)</td>
                            <td className="px-3 py-1.5 text-right">—</td>
                            <td className="px-3 py-1.5 text-right">{pence(e.amountPence)}</td>
                            <td className="px-3 py-1.5 text-indigo-700">+ extra in Stripe</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>
            );
          })()}
        </div>

        {showAdd && (
          <div className="rounded-xl border border-slate-200 p-4 mb-4 space-y-3 bg-slate-50/60">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{editingId === null ? "Add manual entry (bank / expenses / loans)" : "Edit entry"}</h3>
              <button onClick={() => { resetForm(); setShowAdd(false); }}><X size={16} className="text-slate-400" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">Type
                <select value={loanKind} onChange={e => setLoanKind(e.target.value as "" | "loan" | "repayment")} className="mt-1 w-full border rounded px-2 py-1.5">
                  <option value="">Normal entry</option>
                  <option value="loan">Director&apos;s loan received (money in)</option>
                  <option value="repayment">Loan repayment to director (money out)</option>
                </select>
              </label>
              {loanKind ? (
                <label className="text-sm">Lender / director
                  <select value={lenderId} onChange={e => setLenderId(e.target.value ? Number(e.target.value) : "")} className="mt-1 w-full border rounded px-2 py-1.5">
                    <option value="">Choose…</option>
                    {lenders.map(l => <option key={l.id} value={l.id}>{l.name}{l.isDirector ? " (director)" : ""}</option>)}
                  </select>
                </label>
              ) : (
                <label className="text-sm">Direction
                  <select value={dir} onChange={e => setDir(e.target.value as "in" | "out")} className="mt-1 w-full border rounded px-2 py-1.5">
                    <option value="out">Money out (spending)</option>
                    <option value="in">Money in (received)</option>
                  </select>
                </label>
              )}
              <label className="text-sm">Amount (£)
                <input value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm">Date
                <input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm sm:col-span-2">Description
                <input value={desc} onChange={e => setDesc(e.target.value)} placeholder={loanKind ? "e.g. Loan to cover stock" : "e.g. Receipt printer for World Donner"} className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              {!loanKind && (
                <label className="text-sm sm:col-span-2">Category
                  <input value={category} onChange={e => setCategory(e.target.value)} placeholder="printers / marketing / tablets / bank…" className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
              )}
            </div>
            <button onClick={() => void saveManual()} disabled={saving} className="text-sm font-semibold text-white bg-primary px-4 py-2 rounded-lg disabled:opacity-50">
              {saving ? "Saving…" : editingId === null ? "Add to cashbook" : "Save changes"}
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 mb-2 px-1">
          <span>{visibleRows.length} {visibleRows.length === 1 ? "entry" : "entries"}{q && ` matching “${query.trim()}”`}</span>
          {(q || entryType !== "all") && <span>In {pence(filteredIn)} · Out {pence(filteredOut)}</span>}
          <label className="flex items-center gap-2">Entries per page
            <select aria-label="Entries per page" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-slate-700">
              {[25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        </div>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2">
                  <button onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")} className="inline-flex items-center gap-1 hover:text-slate-700" title="Sort by date">
                    Date {sortDir === "desc" ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                  </button>
                </th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Attributed</th>
                <th className="text-center px-2 py-2" title="Confirmed against real Stripe data">Stripe</th>
                <th className="text-right px-3 py-2">In</th>
                <th className="text-right px-3 py-2">Out</th>
                <th className="text-right px-3 py-2">Non-cash</th>
                <th className="px-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center text-slate-400 py-8">Loading…</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-slate-400 py-8">{rows.length === 0 ? "No movements in this range" : "No entries match your filters"}</td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500">{r.date.slice(0, 10)}</td>
                  <td className="px-3 py-2">
                    {r.memo && r.category === "Order income (card)"
                      ? r.description.replace("Recorded net fee projection (non-cash note)", "Recorded fee allocation (non-cash note)")
                      : r.description}
                    {r.memo && r.category === "Order income (card)" && <div className="mt-1 text-xs text-slate-500">May include delivery payments owed to the driver or shop. This is not Hub profit or an additional cash receipt. Check the Stripe receipt or Per Order Match for payment confirmation.</div>}
                    {r.reference && <div className="mt-1 text-xs text-slate-500 break-all">Reference: {r.reference}{r.providerSourceReference ? ` · Stripe source: ${r.providerSourceReference}` : ""}</div>}
                    {r.loanKind && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{r.loanKind === "loan" ? "LOAN" : "REPAYMENT"}</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{r.memo && r.category === "Order income (card)" ? "Fee allocation note" : r.category}</td>
                  <td className="px-3 py-2 text-slate-500">{attribution(r)}</td>
                  <td className="px-2 py-2 text-center">
                    {r.memo
                      ? <span className="text-xs text-slate-500" title="Non-cash note: no separate Stripe receipt is expected for this row. Check the actual Stripe cash movement or Per Order Match.">Non-cash note</span>
                      : r.stripeConfirmed === true
                      ? <span title="Confirmed against real Stripe data" className="text-emerald-600 font-bold">✓</span>
                      : r.stripeConfirmed === false
                        ? <span title="Stripe confirmation unavailable for this entry; this does not establish that payment failed" className="text-rose-500 font-bold">✗</span>
                        : <span className="text-slate-300" title="Not a Stripe card payment (N/A)">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">{!r.memo && r.direction === "in" ? pence(r.amountPence) : ""}</td>
                  <td className="px-3 py-2 text-right font-medium text-rose-700">{!r.memo && r.direction === "out" ? pence(r.amountPence) : ""}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{r.memo ? pence(r.amountPence) : ""}</td>
                  <td className="px-2 whitespace-nowrap">
                    {r.source === "manual" && (
                      <span className="inline-flex items-center gap-1.5">
                        <button onClick={() => startEdit(r)} title="Edit" className="text-slate-300 hover:text-primary"><Pencil size={14} /></button>
                        <button onClick={() => void voidEntry(r.reference)} title="Void" className="text-slate-300 hover:text-rose-600"><X size={14} /></button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <nav aria-label="Cashbook pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-3 py-3 text-sm">
            <span className="text-slate-500" aria-live="polite">{loading ? "Loading…" : visibleRows.length ? `Showing ${pageStart + 1}–${Math.min(pageStart + pageSize, visibleRows.length)} of ${visibleRows.length} entries` : "0 entries"}</span>
            <div className="flex items-center gap-2">
              <button type="button" aria-label="Previous cashbook page" disabled={loading || currentPage === 1} onClick={() => setPage(currentPage - 1)} className="min-h-10 rounded-lg border border-slate-200 bg-white px-3 font-medium disabled:opacity-40">Previous</button>
              <span className="whitespace-nowrap text-slate-600">Page {currentPage} of {pageCount}</span>
              <button type="button" aria-label="Next cashbook page" disabled={loading || currentPage === pageCount} onClick={() => setPage(currentPage + 1)} className="min-h-10 rounded-lg border border-slate-200 bg-white px-3 font-medium disabled:opacity-40">Next</button>
            </div>
          </nav>
        </div>
      </div>
    </AdminLayout>
  );
}
